<template>
    <app-layout title="Add Customer">
        <form @submit.prevent="submit">
            <page-header>
                <template #title>Add Customer</template>

                <template #actions>
                    <template v-if="form.setup_mode">
                        <jet-button v-show="form.isDirty" type="submit" class="h-7 leading-none" :disabled="form.processing" >
                            Save
                        </jet-button>

                        <jet-tertiary-button class="h-7 leading-none" :disabled="form.processing" :href="route('customers.index')">
                            {{ form.isDirty ? 'Cancel' : 'Close' }}
                        </jet-tertiary-button>
                    </template>
                    <jet-tertiary-button v-else class="h-7 leading-none" :href="route('customers.index')">
                        Close
                    </jet-tertiary-button>
                </template>
            </page-header>

            <div class="px-8">
                <div class="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-4">
                    <div class="w-full sm:w-72 shrink-0">
                        <jet-label for="setup_mode" value="Setup Type" />
                        <multiselect
                            id="setup_mode"
                            v-model="form.setup_mode"
                            :options="setupModeOptions"
                            valueProp="value"
                            label="label"
                            placeholder="Select setup type"
                            :canClear="true"
                            :canDeselect="true"
                        />
                        <jet-input-error :message="$page.props.errors.setup_mode" class="mt-1" />
                    </div>
                    <div v-if="form.setup_mode" class="text-sm text-gray-600 mt-1 sm:mt-7">
                        <span v-if="form.setup_mode === 'sales'">
                            Customers created through Sales are automatically activated and can use the Wholesale portal right away.
                        </span>
                        <span v-else-if="form.setup_mode === 'customer'">
                            The new customer will need to have their business details and then payment details approved before being activated and gaining access to the portal.
                        </span>
                    </div>
                    <p v-else class="text-sm text-gray-500 mt-1 sm:mt-7">
                        Select a setup type to continue.
                    </p>
                </div>
            </div>

            <div v-if="form.setup_mode" class="px-8 xl:flex">
                <CustomerCreateCustomer
                    v-if="form.setup_mode === 'customer'"
                    :form="form"
                    :sales-reps="salesReps"
                    :type-options="typeOptions"
                    :pricing-options="pricingOptions"
                />

                <SalesCreateCustomer
                    v-else-if="form.setup_mode === 'sales'"
                    ref="salesCreate"
                    :form="form"
                    :sales-reps="salesReps"
                    :delivery-options="deliveryOptions"
                    :type-options="typeOptions"
                    :route-options="routeOptions"
                    :pricing-options="pricingOptions"
                    :payment-terms-options="paymentTermsOptions"
                    :payment-method-options="paymentMethodOptions"
                    :pricing-products-url="route('customers.pricing_products')"
                />
            </div>
        </form>
    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetButton from '@/Jetstream/Button';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import CustomerForm from '@/Pages/Customer/Partials/CustomerForm';
import CustomerPricingForm from '@/Pages/Customer/Partials/CustomerPricingForm';
import JetLabel from '@/Jetstream/Label';
import Multiselect from '@vueform/multiselect';
import SalesCreateCustomer from '@/Pages/Customer/Partials/SalesCreateCustomer.vue';
import CustomerCreateCustomer from '@/Pages/Customer/Partials/CustomerCreateCustomer.vue';

import '@vueform/multiselect/themes/default.css';
import '@vueform/toggle/themes/default.css';

export default {
    setup() {
        const form = useForm({
            id: null,
            enabled: true,
            sales_rep_id: null,
            pricing_level_id: null,
            tax_exempt: false,
            show_printed_order_pricing: false,
            pallet_orders: false,
            requires_printed_order: false,
            type: null,
            name: null,
            address_1: null,
            address_2: null,
            city: null,
            postal_code: null,
            province: 'Ontario',
            phone: null,
            cell: null,
            fax: null,
            email: null,
            ap_email: null,
            contact_name: null,
            default_shipping_method: 'delivery',
            route: null,
            delivery_schedules: [{ delivery_days: [], delivery_window_start: null, delivery_window_end: null }],
            delivery_duration: null,
            delivery_fee_override: null,
            delivery_order_minimum_override: null,
            delivery_fee_below_minimum_override: null,
            auth_code: null,
            auto_apply_credit: false,
            pallet_orders: false,
            credit_balance: null,
            internal_notes: null,
            order_notes: null,
            picking_notes: null,
            picking_red_flag: true,
            load_notes: null,
            skills: [],
            vehicle_features: [],
            priority: 'M',
            payment_terms: null,
            payment_method: null,
            send_inactive_alerts: false,
            inactive_alert_weeks: null,
            inactive_alert_last_sent_at: null,
            po_num: null,
            invoice_emails: null,
            pricing: [],
            favourite_product_ids: [],
            setup_mode: null,
            users: [{ name: null, email: null }],
        })

        return { form }
    },

    components: {
        AppLayout,
        PageHeader,
        JetButton,
        JetTertiaryButton,
        CustomerForm,
        CustomerPricingForm,
        JetLabel,
        Multiselect,
        SalesCreateCustomer,
        CustomerCreateCustomer,
    },

    props: [
        'salesReps', 
        'pricingOptions', 
        'typeOptions', 
        'routeOptions', 
        'deliveryOptions', 
        'paymentTermsOptions', 
        'paymentMethodOptions'
    ],

    data() {
        return {
            showDeleteModal: false,
            setupModeOptions: [
                { value: 'sales', label: 'Sales' },
                { value: 'customer', label: 'Customer' },
            ],
        }
    },

    methods: {
        submit() {
            if (this.form.setup_mode !== 'customer') {
                delete this.form.users
            }

            const run = () => {
                this.form.post(route('customers.store'), {
                    onError: (errors) => {
                        if (errors?.auth_code) {
                            this.form.auth_code = null;
                            this.$refs.salesCreate?.promptAuthForRetry?.();
                        }
                    },
                });
            };

            if (this.form.setup_mode === 'sales' && this.$refs.salesCreate?.ensureAuthCode) {
                this.$refs.salesCreate.ensureAuthCode(run);
            } else {
                run();
            }
        },
    },
}
</script>

<style scoped>

</style>
